import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Shield, Loader2, X } from "lucide-react";
import { api } from "../../../services/api";

interface Patente {
  id: string;
  patente: string;
  abreviacao: string;
  ordem: number;
  categoria: string;
  img: string | null;
  prazo: number | null;
  id_habbo_variavel?: number | null;
  criado_em: string;
  alterado_em: string;
}

interface FormValues {
  patente: string;
  abreviacao: string;
  ordem: number;
  categoria: string;
  img: string;
  prazo: string;
  id_habbo_variavel: string;
}

interface PatenteModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingPatente?: Patente | null;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  patente: Yup.string()
    .required("Nome da patente é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  abreviacao: Yup.string()
    .max(10, "Abreviação deve ter no máximo 10 caracteres"),
  ordem: Yup.number()
    .required("Ordem é obrigatória")
    .min(1, "Ordem deve ser maior que 0"),
  categoria: Yup.string().required("Categoria é obrigatória"),
  id_habbo_variavel: Yup.string().matches(/^\d*$/, "Informe um número inteiro maior ou igual a 0"),
});

export function PatenteModal({ isOpen, onClose, editingPatente = null, onSuccess }: PatenteModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    patente: "",
    abreviacao: "",
    ordem: 1,
    categoria: "Praça",
    img: "",
    prazo: "",
    id_habbo_variavel: "",
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  useEffect(() => {
    if (editingPatente) {
      setFormData({
        patente: editingPatente.patente,
        abreviacao: editingPatente.abreviacao,
        ordem: editingPatente.ordem,
        categoria: editingPatente.categoria,
        img: editingPatente.img || "",
        prazo: editingPatente.prazo !== null && editingPatente.prazo !== undefined ? String(editingPatente.prazo) : "",
        id_habbo_variavel:
          editingPatente.id_habbo_variavel !== null && editingPatente.id_habbo_variavel !== undefined
            ? String(editingPatente.id_habbo_variavel)
            : "",
      });
    } else {
      setFormData({
        patente: "",
        abreviacao: "",
        ordem: 1,
        categoria: "Praça",
        img: "",
        prazo: "",
        id_habbo_variavel: "",
      });
    }
    setErrors({});
  }, [editingPatente, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: any = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });
      setErrors(validationErrors);
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        ...formData,
        prazo: formData.prazo !== "" ? parseInt(formData.prazo, 10) : null,
        id_habbo_variavel: formData.id_habbo_variavel !== "" ? parseInt(formData.id_habbo_variavel, 10) : null,
      };

      if (editingPatente) {
        await api.put(`/patentes/${editingPatente.id}`, payload);
        enqueueSnackbar("Patente atualizada com sucesso!", { variant: "success" });
      } else {
        await api.post("/patentes", payload);
        enqueueSnackbar("Patente criada com sucesso!", { variant: "success" });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar patente", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ patente: "", abreviacao: "", ordem: 1, categoria: "Praça", img: "", prazo: "", id_habbo_variavel: "" });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingPatente ? "Editar Patente" : "Nova Patente"}
            </CardTitle>
            <CardDescription>
              {editingPatente
                ? "Atualize as informações da patente"
                : "Crie uma nova patente militar"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="patente">Nome da Patente</Label>
              <InputText
                id="patente"
                name="patente"
                title=""
                value={formData.patente}
                onChange={(e) => setFormData({ ...formData, patente: e.target.value })}
                placeholder="Ex: Soldado, Cabo, Sargento..."
                error={errors.patente}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="abreviacao">Abreviação</Label>
              <InputText
                id="abreviacao"
                name="abreviacao"
                title=""
                value={formData.abreviacao}
                onChange={(e) => setFormData({ ...formData, abreviacao: e.target.value })}
                placeholder="Ex: Sd, Cb, Sgt... (opcional)"
                error={errors.abreviacao}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="ordem">Ordem Hierárquica</Label>
              <InputText
                id="ordem"
                name="ordem"
                title=""
                type="number"
                value={formData.ordem.toString()}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 1 })}
                placeholder="1, 2, 3..."
                error={errors.ordem?.toString()}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="categoria">Categoria</Label>
              <select
                id="categoria"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={loading}
              >
                <option value="Praça">Praça</option>
                <option value="Oficial">Oficial</option>
              </select>
            </div>

            <div>
              <Label htmlFor="prazo">Prazo para Promoção (dias)</Label>
              <InputText
                id="prazo"
                name="prazo"
                title=""
                type="number"
                value={formData.prazo}
                onChange={(e) => setFormData({ ...formData, prazo: e.target.value })}
                placeholder="Ex: 14, 30, 60... (opcional)"
                className="mt-1"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Quantidade de dias necessários desde a última promoção. Deixe em branco se não houver prazo.
              </p>
            </div>

            <div>
              <Label htmlFor="id_habbo_variavel">ID da Variável no Habbo</Label>
              <InputText
                id="id_habbo_variavel"
                name="id_habbo_variavel"
                title=""
                type="number"
                min={0}
                value={formData.id_habbo_variavel}
                onChange={(e) => setFormData({ ...formData, id_habbo_variavel: e.target.value })}
                placeholder="Ex: 1, 2, 3... (opcional)"
                error={errors.id_habbo_variavel}
                className="mt-1"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Valor que representa esta patente na variável do jogo. Deixe em branco se não houver.
              </p>
            </div>

            <div>
              <Label htmlFor="img">Imagem da Insígnia (URL)</Label>              <InputText
                id="img"
                name="img"
                title=""
                value={formData.img}
                onChange={(e) => setFormData({ ...formData, img: e.target.value })}
                placeholder="https://... (opcional)"
                className="mt-1"
                disabled={loading}
              />
              {formData.img && (
                <div className="mt-2 flex items-center gap-2">
                  <img
                    src={formData.img}
                    alt="Preview insígnia"
                    className="h-10 w-10 rounded object-contain border border-border bg-muted p-1"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="text-xs text-muted-foreground">Preview</span>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingPatente ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  editingPatente ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
